import { Navigate, Route, Routes } from "react-router-dom";
import { Org } from "~/lib/types";
import { OrganizationPoliciesRoutes } from "~/routes/organization/policies/organizationPolicies-routes";
import { IntegrationsRoutes, PageLayout } from "~/routes";
import {
  OrganizationDashboardPage,
  OrganizationSettingsPage,
  OrganizationSpacesPage,
  UsersListPage,
  UserProfilePage,
} from "~/pages/organization";
import {
  ApiTokens,
  AuditLog,
  Authentication,
  Billing,
  GeneralSettings,
  GenerateServiceAccountsForm,
  LegalCompliance,
  Members,
  ServiceAccounts,
} from "~/pages/organization/settings";
import { roleBillingManager } from "~/lib/iam/roles/BillingManager";
import { useScope } from "~/hooks/useScope";
import { GenerateApiTokenForm } from "~/pages/space-settings/api-tokens/generate-api-token-form";
import { WorkloadIdentity } from "~/pages/space-settings/WorkloadIdentity";
import { WorkloadIdentityProvidersList } from "~/pages/space-settings/WorkloadIdentity/components/WorkloadIdentityProvidersList";
import { WorkloadIdentityProviderForm } from "~/pages/space-settings/WorkloadIdentity/components/WorkloadIdentityProviderForm";
import { FullTextSearchResults } from "~/pages/Search";
import { useCanGetCachedOverview } from "~/lib/iam/actions/mondoo/orgoverview/OrgOverviewService/GetCachedOverview";
import { CreateSpacePage } from "~/pages/organization/CreateSpacePage";
import { useFeatureFlags } from "~/providers/FeatureFlagsProvider";
import { useCanSetOrganizationMembership } from "~/lib/iam/actions/mondoo/captain/Captain/SetOrganizationMembership";
import { useCanListWifAuthBindings } from "~/lib/iam/actions/mondoo/captain/Captain/ListWIFAuthBindings";

type Props = {
  org: Org;
};

export function OrganizationRoutes({ org }: Props) {
  const { organizationScope } = useScope();
  const availablePermissions = organizationScope?.iamActions || [];
  const hasBillingPermissions = roleBillingManager.allowedActions.every(
    (action) => organizationScope?.iamActions.includes(action),
  );
  const { isFeatureEnabled } = useFeatureFlags();
  const canManageOrgMembers = useCanSetOrganizationMembership();
  const canListWifBindings = useCanListWifAuthBindings(organizationScope);

  const canViewOrgOverview = useCanGetCachedOverview(organizationScope);
  const defaultIndexPath = canViewOrgOverview ? "overview" : "spaces";

  // Custom route to selectively restrict access to the billing tab
  function HasBillingAccess() {
    if (hasBillingPermissions) {
      return <Billing {...{ org }} />;
    } else {
      return <Navigate replace to="/general" />;
    }
  }

  // TODO:: add meaningful handling
  if (!organizationScope) return;

  return (
    <Routes>
      <Route element={<PageLayout />}>
        <>
          <Route
            index
            element={
              <Navigate
                to={`${defaultIndexPath}?organizationId=${org.id}`}
                replace
              />
            }
          />
          <Route
            path="overview"
            element={<OrganizationDashboardPage org={org} />}
          />
          <Route path="spaces">
            <Route index element={<OrganizationSpacesPage org={org} />} />
            <Route path="new" element={<CreateSpacePage />} />
          </Route>
          {isFeatureEnabled("User Management") && canManageOrgMembers && (
            <Route path="users">
              <Route index element={<UsersListPage org={org} />} />
              <Route path=":userMrn" element={<UserProfilePage org={org} />} />
            </Route>
          )}
          <Route
            path="settings"
            element={
              <OrganizationSettingsPage {...{ org, availablePermissions }} />
            }
          >
            <Route index element={<GeneralSettings />} />
            <Route path="general" element={<GeneralSettings />} />
            <Route path="members/*" element={<Members />} />
            <Route path="auditlog" element={<AuditLog />} />
            <Route path="authentication" element={<Authentication />} />
            <Route path="billing" element={<HasBillingAccess />} />
            <Route path="legal" element={<LegalCompliance />} />
            <Route path="api-tokens" element={<ApiTokens />} />
            <Route
              path="api-tokens/add"
              element={<GenerateApiTokenForm {...{ org }} />}
            />
            <Route
              path="serviceaccounts"
              element={<ServiceAccounts {...{ org }} />}
            />
            <Route
              path="serviceaccounts/add"
              element={<GenerateServiceAccountsForm {...{ org }} />}
            />
            {canListWifBindings && organizationScope && (
              <Route path="workload-identity">
                <Route
                  index
                  element={<WorkloadIdentity space={organizationScope} />}
                />
                <Route path="providers">
                  <Route
                    index
                    element={
                      <WorkloadIdentityProvidersList
                        scope={organizationScope}
                      />
                    }
                  />
                  <Route
                    path="add/:type"
                    element={
                      <WorkloadIdentityProviderForm scope={organizationScope} />
                    }
                  />
                </Route>
              </Route>
            )}
          </Route>

          {/* Integrations Routes */}
          <Route
            path="integrations/*"
            element={<IntegrationsRoutes {...{ scope: organizationScope }} />}
          />

          {/* Organization Policies Routes */}
          <Route path="policies/*" element={<OrganizationPoliciesRoutes />} />
        </>
      </Route>

      <Route element={<PageLayout maxWidth={false} />}>
        <Route path="search" element={<FullTextSearchResults org={org} />} />
      </Route>
    </Routes>
  );
}
